
import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Line, 
  LineChart, 
  CartesianGrid, 
  XAxis, 
  YAxis, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { 
  Calendar as CalendarIcon, 
  ArrowDownUp, 
  AlertCircle, 
  TrendingDown, 
  Archive, 
  FileText, 
  Bell,
  DollarSign,
  TrendingUp,
  Users,
  ShoppingCart,
  Clock,
  PlusCircle,
  ArrowRight
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";

// Sample data for price seasonality
const seasonalityData = [
  { month: 'Jan', beefPrice: 8.2, tomatoPrice: 3.9, flourPrice: 1.2, cheesePrice: 9.1 },
  { month: 'Feb', beefPrice: 8.3, tomatoPrice: 4.2, flourPrice: 1.2, cheesePrice: 9.0 },
  { month: 'Mar', beefPrice: 8.1, tomatoPrice: 4.0, flourPrice: 1.1, cheesePrice: 9.2 },
  { month: 'Apr', beefPrice: 8.0, tomatoPrice: 3.7, flourPrice: 1.1, cheesePrice: 9.3 },
  { month: 'May', beefPrice: 8.2, tomatoPrice: 3.4, flourPrice: 1.2, cheesePrice: 9.4 },
  { month: 'Jun', beefPrice: 8.4, tomatoPrice: 2.9, flourPrice: 1.2, cheesePrice: 9.5 },
  { month: 'Jul', beefPrice: 8.6, tomatoPrice: 2.5, flourPrice: 1.3, cheesePrice: 9.6 },
  { month: 'Aug', beefPrice: 8.7, tomatoPrice: 2.2, flourPrice: 1.3, cheesePrice: 9.5 },
  { month: 'Sep', beefPrice: 8.5, tomatoPrice: 2.5, flourPrice: 1.3, cheesePrice: 9.4 },
  { month: 'Oct', beefPrice: 8.3, tomatoPrice: 2.9, flourPrice: 1.2, cheesePrice: 9.3 },
  { month: 'Nov', beefPrice: 8.2, tomatoPrice: 3.4, flourPrice: 1.2, cheesePrice: 9.2 },
  { month: 'Dec', beefPrice: 8.1, tomatoPrice: 3.7, flourPrice: 1.1, cheesePrice: 9.1 },
];

// Sample data for bulk opportunities
const bulkOpportunities = [
  { 
    id: 1, 
    ingredient: 'Cabra Canaria', 
    currentPrice: 7.49, 
    promotionalPrice: 5.99, 
    supplier: 'Carnicería Atlántica',
    discount: 20,
    expirationDate: '2023-06-15',
    minimumQuantity: '50 lbs',
    savings: 75,
    seasonalTrend: 'increasing'
  },
  { 
    id: 2, 
    ingredient: 'Tomates de Lanzarote', 
    currentPrice: 3.99, 
    promotionalPrice: 2.79, 
    supplier: 'Finca Agrícola Canaria',
    discount: 30,
    expirationDate: '2023-06-10',
    minimumQuantity: '30 lbs',
    savings: 36,
    seasonalTrend: 'stable'
  },
  { 
    id: 3, 
    ingredient: 'Harina de Gofio', 
    currentPrice: 2.49, 
    promotionalPrice: 1.89, 
    supplier: 'Molinos Tenerife',
    discount: 24,
    expirationDate: '2023-06-30',
    minimumQuantity: '100 lbs',
    savings: 60,
    seasonalTrend: 'decreasing'
  }
];

// Sample data for scheduled purchases
const purchaseSchedule = [
  {
    id: 1,
    ingredient: 'Carne de Cordero',
    quantity: '40 lbs',
    estimatedPrice: 399.60,
    purchaseDate: '2023-06-15',
    supplier: 'Carnicería Atlántica',
    notes: 'Price favorable in June',
    status: 'scheduled'
  },
  {
    id: 2,
    ingredient: 'Nata Montada',
    quantity: '20 gallons',
    estimatedPrice: 159.80,
    purchaseDate: '2023-06-10',
    supplier: 'Lácteos Guanche',
    notes: 'Monthly standing order',
    status: 'recurring'
  },
  {
    id: 3,
    ingredient: 'Tomates Canarios',
    quantity: '75 lbs',
    estimatedPrice: 187.50,
    purchaseDate: '2023-06-22',
    supplier: 'Finca Agrícola Canaria',
    notes: 'Price dip expected',
    status: 'recommended'
  }
];

// Sample data for contract recommendations
const contractRecommendations = [
  {
    id: 1,
    ingredient: 'Carne de Cabra',
    category: 'Meat',
    currentSpot: 7.49,
    forecastedPrice: 8.65,
    increasePercent: 15,
    recommendedTerm: '6 months',
    potentialSavings: 1368,
    supplier: 'Carnicería Atlántica',
    confidence: 'high'
  },
  {
    id: 2,
    ingredient: 'Mantequilla de La Palma',
    category: 'Dairy',
    currentSpot: 4.29,
    forecastedPrice: 5.15,
    increasePercent: 20,
    recommendedTerm: '3 months',
    potentialSavings: 516,
    supplier: 'Lácteos Guanche',
    confidence: 'medium'
  },
  {
    id: 3,
    ingredient: 'Aceite de Oliva',
    category: 'Dry Goods',
    currentSpot: 15.99,
    forecastedPrice: 18.39,
    increasePercent: 15,
    recommendedTerm: '12 months',
    potentialSavings: 1152,
    supplier: 'Importaciones El Teide',
    confidence: 'high'
  }
];

// Sample data for group purchase opportunities
const groupPurchaseOpportunities = [
  {
    id: 1,
    ingredient: 'Vieja Fresca',
    currentPrice: 19.99,
    groupPrice: 15.99,
    volumeRequired: '200 lbs',
    partnersNeeded: 2,
    supplier: 'Pescados Isleños',
    discountPercent: 20,
    totalSavings: 800,
    deadline: '2023-06-20'
  },
  {
    id: 2,
    ingredient: 'Barriles de Cerveza Tropical',
    currentPrice: 199.00,
    groupPrice: 169.00,
    volumeRequired: '15 kegs',
    partnersNeeded: 3,
    supplier: 'Cervecería Isleña',
    discountPercent: 15,
    totalSavings: 450,
    deadline: '2023-07-01'
  }
];

// Sample data for historical price data
const priceHistoryData = [
  { month: 'Jan', price: 8.20, avgPrice: 8.30 },
  { month: 'Feb', price: 8.30, avgPrice: 8.30 },
  { month: 'Mar', price: 8.10, avgPrice: 8.25 },
  { month: 'Apr', price: 8.00, avgPrice: 8.20 },
  { month: 'May', price: 8.20, avgPrice: 8.15 },
  { month: 'Jun', price: 8.40, avgPrice: 8.10 },
  { month: 'Jul', price: 8.60, avgPrice: 8.05 },
  { month: 'Aug', price: 8.70, avgPrice: 8.00 },
  { month: 'Sep', price: 8.50, avgPrice: 8.05 },
  { month: 'Oct', price: 8.30, avgPrice: 8.10 },
  { month: 'Nov', price: 8.20, avgPrice: 8.15 },
  { month: 'Dec', price: 8.10, avgPrice: 8.20 },
];



const ProcurementCalendarOptimizer = () => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [selectedIngredient, setSelectedIngredient] = useState("beef");

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-3xl font-semibold tracking-tight">Procurement Calendar Optimizer</h1>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <Select defaultValue="beef">
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Select ingredient" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="beef">Beef</SelectItem>
              <SelectItem value="tomato">Tomatoes</SelectItem>
              <SelectItem value="flour">Flour</SelectItem>
              <SelectItem value="cheese">Cheese</SelectItem>
            </SelectContent>
          </Select>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full sm:w-[240px] justify-start">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? format(selectedDate, "MMMM yyyy") : "Select month"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          
          <Button variant="secondary">
            <FileText className="mr-2 h-4 w-4" />
            Export Calendar
          </Button>
        </div>
      </div>

      <Tabs defaultValue="seasonality" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="seasonality">Price Seasonality</TabsTrigger>
          <TabsTrigger value="schedule">Purchase Schedule</TabsTrigger>
          <TabsTrigger value="opportunities">Bulk Opportunities</TabsTrigger>
          <TabsTrigger value="contracts">Advanced Contracts</TabsTrigger>
          <TabsTrigger value="group">Group Purchasing</TabsTrigger>
        </TabsList>
        
        <TabsContent value="seasonality" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Price Seasonality Analysis</CardTitle>
              <CardDescription>
                Track historical and projected prices for key ingredients throughout the year
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={seasonalityData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value) => [`$${value}`, 'Price']}
                      contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        border: 'none'
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="beefPrice"
                      name="Beef"
                      stroke="#ef4444"
                      activeDot={{ r: 8 }}
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="tomatoPrice"
                      name="Tomatoes"
                      stroke="#22c55e"
                      activeDot={{ r: 8 }}
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="flourPrice"
                      name="Flour"
                      stroke="#f59e0b"
                      activeDot={{ r: 8 }}
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="cheesePrice"
                      name="Cheese"
                      stroke="#3b82f6"
                      activeDot={{ r: 8 }}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Optimal Purchase Windows</CardTitle>
                <CardDescription>
                  Recommended buying periods for price-sensitive ingredients
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <h4 className="font-medium">Beef</h4>
                      <span className="text-sm text-muted-foreground">Current: $8.20/lb</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-muted h-3 flex-1 rounded-full overflow-hidden">
                        <div className="bg-green-500 h-full w-[25%]" />
                      </div>
                      <span className="text-sm text-green-600 font-medium">Apr-May</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      4% below average price during this period.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <h4 className="font-medium">Tomatoes</h4>
                      <span className="text-sm text-muted-foreground">Current: $3.40/lb</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-muted h-3 flex-1 rounded-full overflow-hidden">
                        <div className="bg-green-500 h-full w-[50%]" />
                      </div>
                      <span className="text-sm text-green-600 font-medium">Jul-Sep</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      35% below average price during this period.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <h4 className="font-medium">Flour</h4>
                      <span className="text-sm text-muted-foreground">Current: $1.20/lb</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-muted h-3 flex-1 rounded-full overflow-hidden">
                        <div className="bg-green-500 h-full w-[33%]" />
                      </div>
                      <span className="text-sm text-green-600 font-medium">Mar-May</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      8% below average price during this period.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <h4 className="font-medium">Cheese</h4>
                      <span className="text-sm text-muted-foreground">Current: $9.30/lb</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="bg-muted h-3 flex-1 rounded-full overflow-hidden">
                        <div className="bg-green-500 h-full w-[16%]" />
                      </div>
                      <span className="text-sm text-green-600 font-medium">Dec-Feb</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      5% below average price during this period.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Price History: {selectedIngredient.charAt(0).toUpperCase() + selectedIngredient.slice(1)}</CardTitle>
                <CardDescription>
                  Historical price data vs. market average
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={priceHistoryData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis domain={['dataMin - 0.5', 'dataMax + 0.5']} />
                      <Tooltip 
                        formatter={(value) => [`$${value}`, 'Price']}
                        contentStyle={{
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          border: 'none'
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="price"
                        name="Your Paid Price"
                        stroke="#3b82f6"
                        strokeWidth={2}
                      />
                      <Line
                        type="monotone"
                        dataKey="avgPrice"
                        name="Market Average"
                        stroke="#9ca3af"
                        strokeDasharray="3 3"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 flex justify-between items-center text-sm">
                  <div className="flex flex-col">
                    <span className="text-muted-foreground">Annual Savings Opportunity</span>
                    <span className="font-medium text-green-600">$720.00</span>
                  </div>
                  <Button variant="outline" size="sm">
                    View Detailed Analysis
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="schedule" className="mt-6 space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle>Optimized Purchase Schedule</CardTitle>
                <CardDescription>
                  Your upcoming scheduled purchases based on price forecasts
                </CardDescription>
              </div>
              <Button size="sm">
                <PlusCircle className="mr-2 h-4 w-4" />
                New Schedule
              </Button>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="py-3 px-4 text-left font-medium">Ingredient</th>
                      <th className="py-3 px-4 text-left font-medium">Quantity</th>
                      <th className="py-3 px-4 text-center font-medium">Purchase Date</th>
                      <th className="py-3 px-4 text-right font-medium">Est. Price</th>
                      <th className="py-3 px-4 text-center font-medium">Status</th>
                      <th className="py-3 px-4 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {purchaseSchedule.map((purchase) => (
                      <tr key={purchase.id} className="hover:bg-muted/30">
                        <td className="py-3 px-4">
                          <div className="font-medium">{purchase.ingredient}</div>
                          <div className="text-xs text-muted-foreground">{purchase.supplier}</div>
                        </td>
                        <td className="py-3 px-4">{purchase.quantity}</td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex flex-col items-center">
                            <span>{purchase.purchaseDate}</span>
                            <span className="text-xs text-muted-foreground">{purchase.notes}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right font-medium">${purchase.estimatedPrice.toFixed(2)}</td>
                        <td className="py-3 px-4 text-center">
                          {purchase.status === 'scheduled' && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              Scheduled
                            </Badge>
                          )}
                          {purchase.status === 'recurring' && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                              Recurring
                            </Badge>
                          )}
                          {purchase.status === 'recommended' && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              Recommended
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button variant="ghost" size="sm">
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Price Variability</CardTitle>
                <CardDescription>
                  Ingredients with highest price fluctuations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">Tomatoes</span>
                      <span className="text-xs font-medium text-amber-600">±35%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="bg-amber-500 h-full" style={{ width: '85%' }} />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">Avocados</span>
                      <span className="text-xs font-medium text-amber-600">±28%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="bg-amber-500 h-full" style={{ width: '70%' }} />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">Seafood</span>
                      <span className="text-xs font-medium text-amber-600">±22%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="bg-amber-500 h-full" style={{ width: '55%' }} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Forecasted Changes</CardTitle>
                <CardDescription>
                  Predicted price changes next 30 days
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Beef Chuck</p>
                      <p className="text-xs text-muted-foreground">
                        Current: $7.49/lb
                      </p>
                    </div>
                    <Badge className="bg-red-100 text-red-800 hover:bg-red-200 flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      +6%
                    </Badge>
                  </li>
                  
                  <li className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Tomatoes</p>
                      <p className="text-xs text-muted-foreground">
                        Current: $3.49/lb
                      </p>
                    </div>
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-200 flex items-center gap-1">
                      <TrendingDown className="h-3 w-3" />
                      -12%
                    </Badge>
                  </li>
                  
                  <li className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Heavy Cream</p>
                      <p className="text-xs text-muted-foreground">
                        Current: $4.29/qt
                      </p>
                    </div>
                    <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-200 flex items-center gap-1">
                      Stable
                    </Badge>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Budget Allocation</CardTitle>
                <CardDescription>
                  Estimated spend by category
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: 'Meat', value: 38 },
                        { name: 'Produce', value: 22 },
                        { name: 'Dairy', value: 16 },
                        { name: 'Dry', value: 14 },
                        { name: 'Other', value: 10 },
                      ]}
                      margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
                    >
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => [`${value}%`, 'Budget']}
                        contentStyle={{
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          border: 'none'
                        }}
                      />
                      <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  Based on your optimal purchase schedule for this month.
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="opportunities" className="mt-6 space-y-6">
          <div className="grid md:grid-cols-3 gap-6">
            {bulkOpportunities.map((opportunity) => (
              <Card key={opportunity.id} className="overflow-hidden">
                <div className="h-2 bg-blue-500" />
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg">{opportunity.ingredient}</h3>
                        <p className="text-muted-foreground text-sm">{opportunity.supplier}</p>
                      </div>
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200">Limited Time</Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Regular Price</p>
                        <p className="font-semibold line-through text-muted-foreground">${opportunity.currentPrice.toFixed(2)}/lb</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Promotional Price</p>
                        <p className="font-semibold text-green-600">${opportunity.promotionalPrice.toFixed(2)}/lb</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <Badge variant="outline" className="flex items-center bg-green-50 text-green-700 border-green-200">
                        <DollarSign className="h-3 w-3 mr-1" />
                        {opportunity.discount}% Off
                      </Badge>
                      <div className="flex flex-col items-end">
                        <span className="text-xs text-muted-foreground">Potential Savings</span>
                        <span className="font-semibold text-green-600">${opportunity.savings.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <div className="space-y-1 border-t pt-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Minimum Order:</span>
                        <span>{opportunity.minimumQuantity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Expires:</span>
                        <span>{opportunity.expirationDate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Seasonal Trend:</span>
                        <span className="flex items-center">
                          {opportunity.seasonalTrend === 'increasing' && (
                            <><TrendingUp className="h-3 w-3 mr-1 text-red-500" /> Increasing</>
                          )}
                          {opportunity.seasonalTrend === 'decreasing' && (
                            <><TrendingDown className="h-3 w-3 mr-1 text-green-500" /> Decreasing</>
                          )}
                          {opportunity.seasonalTrend === 'stable' && (
                            <>Stable</>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
                <div className="bg-muted/30 p-3 flex justify-between">
                  <Button variant="ghost" size="sm">
                    <Bell className="h-4 w-4 mr-2" />
                    Remind Me
                  </Button>
                  <Button size="sm">
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Order Now
                  </Button>
                </div>
              </Card>
            ))}
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Set Bulk Purchase Alerts</CardTitle>
              <CardDescription>
                Get notified when prices drop below your thresholds
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="ingredient">Ingredient</Label>
                  <Select defaultValue="beef">
                    <SelectTrigger id="ingredient">
                      <SelectValue placeholder="Select ingredient" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beef">Beef</SelectItem>
                      <SelectItem value="tomato">Tomatoes</SelectItem>
                      <SelectItem value="flour">Flour</SelectItem>
                      <SelectItem value="cheese">Cheese</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-3">
                  <Label htmlFor="threshold">Price Threshold</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input id="threshold" type="number" placeholder="0.00" step="0.01" min="0" className="pl-9" />
                  </div>
                </div>
                
                <div className="space-y-3">
                  <Label htmlFor="quantity">Minimum Quantity</Label>
                  <div className="flex">
                    <Input id="quantity" type="number" placeholder="0" step="1" min="0" />
                    <Select defaultValue="lbs">
                      <SelectTrigger className="w-[80px] ml-2">
                        <SelectValue placeholder="Unit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lbs">lbs</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="qt">qt</SelectItem>
                        <SelectItem value="each">each</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <Button className="mt-6">
                <Bell className="mr-2 h-4 w-4" />
                Create Alert
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="contracts" className="mt-6 space-y-6">
          <div className="grid md:grid-cols-3 gap-6">
            {contractRecommendations.map((recommendation) => (
              <Card key={recommendation.id} className="overflow-hidden">
                <div className="h-2 bg-purple-500" />
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-lg">{recommendation.ingredient}</h3>
                      <p className="text-muted-foreground text-sm">{recommendation.category}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Current Spot Price</p>
                        <p className="font-semibold">${recommendation.currentSpot.toFixed(2)}/lb</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Forecasted Price</p>
                        <p className="font-semibold text-red-500">${recommendation.forecastedPrice.toFixed(2)}/lb</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <Badge className="bg-red-100 text-red-800 flex items-center">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        {recommendation.increasePercent}% increase predicted
                      </Badge>
                    </div>
                    
                    <div className="space-y-1 border-t pt-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Supplier:</span>
                        <span>{recommendation.supplier}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Recommended Term:</span>
                        <span>{recommendation.recommendedTerm}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Forecast Confidence:</span>
                        <Badge variant="outline" className={
                          recommendation.confidence === 'high' 
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : recommendation.confidence === 'medium'
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                        }>
                          {recommendation.confidence.charAt(0).toUpperCase() + recommendation.confidence.slice(1)}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center pt-2">
                      <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground">Potential Savings</span>
                        <span className="font-semibold text-green-600">${recommendation.potentialSavings.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
                <div className="bg-muted/30 p-3 flex justify-end">
                  <Button size="sm">
                    Request Contract <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Contract Analysis</CardTitle>
              <CardDescription>
                Comparing spot prices vs. contract prices over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={[
                      { month: 'Jan', spot: 8.20, contract: 8.10 },
                      { month: 'Feb', spot: 8.30, contract: 8.10 },
                      { month: 'Mar', spot: 8.10, contract: 8.10 },
                      { month: 'Apr', spot: 8.00, contract: 8.10 },
                      { month: 'May', spot: 8.20, contract: 8.10 },
                      { month: 'Jun', spot: 8.40, contract: 8.10 },
                      { month: 'Jul', spot: 8.60, contract: 8.10 },
                      { month: 'Aug', spot: 8.70, contract: 8.10 },
                      { month: 'Sep', spot: 8.50, contract: 8.10 },
                      { month: 'Oct', spot: 8.30, contract: 8.10 },
                      { month: 'Nov', spot: 8.20, contract: 8.10 },
                      { month: 'Dec', spot: 8.10, contract: 8.10 },
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis domain={[7.5, 9]} />
                    <Tooltip 
                      formatter={(value) => [`$${value}`, 'Price']}
                      contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        border: 'none'
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="spot"
                      name="Spot Price"
                      stroke="#ef4444"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="contract"
                      name="Contract Price"
                      stroke="#3b82f6"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="group" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {groupPurchaseOpportunities.map((opportunity) => (
              <Card key={opportunity.id} className="overflow-hidden">
                <div className="h-2 bg-green-500" />
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg">{opportunity.ingredient}</h3>
                        <p className="text-muted-foreground text-sm">{opportunity.supplier}</p>
                      </div>
                      <Badge className="bg-green-100 text-green-800 border-green-200">Group Buy</Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Individual Price</p>
                        <p className="font-semibold line-through text-muted-foreground">${opportunity.currentPrice.toFixed(2)}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Group Price</p>
                        <p className="font-semibold text-green-600">${opportunity.groupPrice.toFixed(2)}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Volume Required:</span>
                        <span>{opportunity.volumeRequired}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Partners Needed:</span>
                        <span>{opportunity.partnersNeeded}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Deadline:</span>
                        <span>{opportunity.deadline}</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center pt-2">
                      <Badge variant="outline" className="flex items-center bg-green-50 text-green-700 border-green-200">
                        <DollarSign className="h-3 w-3 mr-1" />
                        {opportunity.discountPercent}% Off
                      </Badge>
                      <div className="flex flex-col items-end">
                        <span className="text-xs text-muted-foreground">Group Savings</span>
                        <span className="font-semibold text-green-600">${opportunity.totalSavings.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
                <div className="bg-muted/30 p-3 flex justify-between">
                  <Button variant="outline" size="sm">
                    <Users className="h-4 w-4 mr-2" />
                    Find Partners
                  </Button>
                  <Button size="sm">
                    Join Group <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Create Group Purchase</CardTitle>
              <CardDescription>
                Coordinate with other restaurants for volume discounts
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="group-ingredient">Ingredient</Label>
                  <Select defaultValue="salmon">
                    <SelectTrigger id="group-ingredient">
                      <SelectValue placeholder="Select ingredient" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="salmon">Wild Caught Salmon</SelectItem>
                      <SelectItem value="beef">Prime Beef Tenderloin</SelectItem>
                      <SelectItem value="oil">Extra Virgin Olive Oil</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-3">
                  <Label htmlFor="group-volume">Total Volume</Label>
                  <div className="flex">
                    <Input id="group-volume" type="number" placeholder="0" step="1" min="0" />
                    <Select defaultValue="lbs">
                      <SelectTrigger className="w-[80px] ml-2">
                        <SelectValue placeholder="Unit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lbs">lbs</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="cases">cases</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <Label htmlFor="group-partners">Minimum Partners</Label>
                  <Input id="group-partners" type="number" placeholder="2" step="1" min="2" />
                </div>
                
                <div className="space-y-3">
                  <Label htmlFor="group-deadline">Deadline</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start" id="group-deadline">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, "MMM dd, yyyy") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              
              <Button className="mt-6">
                <Users className="mr-2 h-4 w-4" />
                Create Group Purchase
              </Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Your Restaurant Network</CardTitle>
              <CardDescription>
                Nearby restaurants to coordinate purchases with
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="py-3 px-4 text-left font-medium">Restaurant</th>
                      <th className="py-3 px-4 text-left font-medium">Distance</th>
                      <th className="py-3 px-4 text-left font-medium">Past Collaborations</th>
                      <th className="py-3 px-4 text-center font-medium">Status</th>
                      <th className="py-3 px-4 text-right font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr className="hover:bg-muted/30">
                      <td className="py-3 px-4">
                        <div className="font-medium">The Rustic Spoon</div>
                        <div className="text-xs text-muted-foreground">Farm to table cuisine</div>
                      </td>
                      <td className="py-3 px-4">1.2 miles</td>
                      <td className="py-3 px-4">12 group purchases</td>
                      <td className="py-3 px-4 text-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active Partner</Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="sm">Contact</Button>
                      </td>
                    </tr>
                    <tr className="hover:bg-muted/30">
                      <td className="py-3 px-4">
                        <div className="font-medium">Bistro Moderne</div>
                        <div className="text-xs text-muted-foreground">Contemporary French</div>
                      </td>
                      <td className="py-3 px-4">2.7 miles</td>
                      <td className="py-3 px-4">5 group purchases</td>
                      <td className="py-3 px-4 text-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active Partner</Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="sm">Contact</Button>
                      </td>
                    </tr>
                    <tr className="hover:bg-muted/30">
                      <td className="py-3 px-4">
                        <div className="font-medium">Seaside Grill</div>
                        <div className="text-xs text-muted-foreground">Seafood specialist</div>
                      </td>
                      <td className="py-3 px-4">3.5 miles</td>
                      <td className="py-3 px-4">2 group purchases</td>
                      <td className="py-3 px-4 text-center">
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Occasional</Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="sm">Contact</Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProcurementCalendarOptimizer;
